import React, { useState } from 'react';
import { Upload, Download, Calendar } from 'lucide-react';
import * as XLSX from 'xlsx';
import Backlog from './Backlog';
import Custom from './Custom';

export default function MSQOrderTracker() {
  const [showApp, setShowApp] = useState(false);
  const [activeTab, setActiveTab] = useState('backlog');
  const [data, setData] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [fileName, setFileName] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedShipTo, setSelectedShipTo] = useState('all');
  const [selectedItem, setSelectedItem] = useState('all');
  const [shipToOptions, setShipToOptions] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);

  const [backlogItems, setBacklogItems] = useState([]);
  const [showBacklogForm, setShowBacklogForm] = useState(false);
  const [backlogForm, setBacklogForm] = useState({
    customer: '',
    orderNumber: '',
    msq: '',
    dueDate: '',
    notes: '',
    priority: 'medium'
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const processedData = jsonData
        .map((row) => {
          const qty = parseFloat(row['MSQ Ordered']);

          return {
            custPO: row['Cust. P/O Number'] || '',
            itemIdent: row['Item Ident'] || '',
            itemNo: row['ItemNo'] || '',
            orderNumber: row['Order#'] || '',
            qty: isNaN(qty) ? 0 : qty,
            dueDate: row['Due Date'] || '',
            status: row['Status'] || '',
            dateRequested: row['Request Date'] || '',
            shipTo: row['ShipTo Info'] || ''
          };
        })
        .filter(row => row.dueDate)
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

      setData(processedData);
      setFileName(file.name);

      const uniqueShipTo = [...new Set(processedData.map(row => row.shipTo).filter(Boolean))];
      setShipToOptions(uniqueShipTo);
      
      const uniqueItems = [...new Set(processedData.map(row => row.itemIdent).filter(Boolean))];
      setItemOptions(uniqueItems);

      applyFilters(processedData, dateFilter, selectedShipTo, selectedItem);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please make sure it\'s a valid Excel file.');
    }
  };

  const applyFilters = (dataToFilter, dateFilterVal, shipToVal, itemVal) => {
    let filteredData = [...dataToFilter];

    if (dateFilterVal === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filteredData = filteredData.filter(row => {
        const dueDate = new Date(row.dueDate);
        return dueDate >= today;
      });
    }

    if (shipToVal !== 'all') {
      filteredData = filteredData.filter(row => row.shipTo === shipToVal);
    }
    
    if (itemVal !== 'all') {
      filteredData = filteredData.filter(row => row.itemIdent === itemVal);
    }

    const grouped = filteredData.reduce((acc, row) => {
      const date = row.dueDate;
      if (!acc[date]) {
        acc[date] = { orders: [], totalMSQ: 0 };
      }
      acc[date].orders.push(row);
      acc[date].totalMSQ += row.qty;
      return acc;
    }, {});
    
    setGroupedData(grouped);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    applyFilters(data, filter, selectedShipTo, selectedItem);
  };

  const handleShipToChange = (shipTo) => {
    setSelectedShipTo(shipTo);
    applyFilters(data, dateFilter, shipTo, selectedItem);
  };
  
  const handleItemChange = (item) => {
    setSelectedItem(item);
    applyFilters(data, dateFilter, selectedShipTo, item);
  };

  const handleBacklogSubmit = () => {
    const newItem = {
      id: Date.now(),
      ...backlogForm,
      createdAt: new Date().toISOString()
    };

    setBacklogItems(prev => [...prev, newItem]);

    setBacklogForm({
      customer: '',
      orderNumber: '',
      msq: '',
      dueDate: '',
      notes: '',
      priority: 'medium'
    });
    setShowBacklogForm(false);
  };

  const deleteBacklogItem = (id) => {
    setBacklogItems(prev => prev.filter(item => item.id !== id));
  };

  const downloadExcel = () => {
    const exportData = [];
    
    Object.keys(groupedData).forEach(key => {
      const groupData = groupedData[key];
      
      exportData.push({
        'Due Date': key,
        'Total MSQ': groupData.totalMSQ,
        'Order Count': groupData.orders.length,
        'Summary': '--- DAY SUMMARY ---'
      });

      groupData.orders.forEach(order => {
        exportData.push({
          'Due Date': '',
          'Total MSQ': '',
          'Order Count': '',
          'Cust P/O Number': order.custPO,
          'Ship To': order.shipTo,
          'Item Ident': order.itemIdent,
          'Order#': order.orderNumber,
          'Request Date': order.dateRequested,
          'Item No': order.itemNo,
          'QTY': order.qty,
          'Status': order.status
        });
      });

      exportData.push({});
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders Summary');
    XLSX.writeFile(wb, `Orders_Summary_by_Day.xlsx`);
  };

  // Dashboard calculations
  const getDashboardStats = () => {
    if (data.length === 0) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Filter only orders from today onwards
    const futureOrders = data.filter(order => {
      const orderDate = new Date(order.dueDate);
      return orderDate >= today;
    });

    if (futureOrders.length === 0) return null;
    
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    const weekOrders = futureOrders.filter(order => {
      const date = new Date(order.dueDate);
      return date <= nextWeek;
    });

    const monthOrders = futureOrders.filter(order => {
      const date = new Date(order.dueDate);
      return date <= nextMonth;
    });

    const weekMSQ = weekOrders.reduce((sum, order) => sum + order.qty, 0);
    const monthMSQ = monthOrders.reduce((sum, order) => sum + order.qty, 0);
    const totalMSQ = futureOrders.reduce((sum, order) => sum + order.qty, 0);

    // Filter grouped data for future dates only
    const futureGroupedData = Object.entries(groupedData).filter(([date]) => {
      const d = new Date(date);
      return d >= today;
    });

    const futureDailyTotals = futureGroupedData.map(([_, day]) => day.totalMSQ);
    const avgDailyMSQ = futureDailyTotals.length > 0 ? futureDailyTotals.reduce((a, b) => a + b, 0) / futureDailyTotals.length : 0;
    const peakMSQ = Math.max(...futureDailyTotals, 0);

    const highCapacityDays = futureGroupedData.filter(([_, day]) => day.totalMSQ >= 700);
    const overCapacityDays = futureGroupedData.filter(([_, day]) => day.totalMSQ >= 800);

    const topItems = {};
    futureOrders.forEach(order => {
      if (order.itemIdent) {
        topItems[order.itemIdent] = (topItems[order.itemIdent] || 0) + order.qty;
      }
    });
    const sortedItems = Object.entries(topItems).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const topShipTo = {};
    futureOrders.forEach(order => {
      if (order.shipTo) {
        topShipTo[order.shipTo] = (topShipTo[order.shipTo] || 0) + order.qty;
      }
    });
    const sortedShipTo = Object.entries(topShipTo).sort((a, b) => b[1] - a[1]).slice(0, 5);

    return {
      weekMSQ,
      monthMSQ,
      totalMSQ,
      avgDailyMSQ,
      peakMSQ,
      totalOrders: futureOrders.length,
      weekOrders: weekOrders.length,
      monthOrders: monthOrders.length,
      highCapacityDays: highCapacityDays.length,
      overCapacityDays: overCapacityDays.length,
      topItems: sortedItems,
      topShipTo: sortedShipTo,
      upcomingHighCapacity: highCapacityDays.slice(0, 5),
      futureGroupedData: futureGroupedData
    };
  };

  const stats = getDashboardStats();

  // Home Page Component
  if (!showApp) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-green-600 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-12 text-center transform hover:scale-105 transition-transform duration-300">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <button
                onClick={() => setShowApp(true)}
                className="transform hover:scale-110 transition-transform duration-300 focus:outline-none focus:ring-4 focus:ring-blue-400 rounded-2xl"
              >
                <img src="/YL.png" alt="York Container Logo" className="w-48 h-48 object-contain" />
              </button>
            </div>

            {/* Title */}
            <h1 className="text-5xl font-bold mb-4">
              <span className="text-blue-600">YORK</span>
              <span className="text-green-500"> CONTAINER</span>
            </h1>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">MW Order Tracker</h2>
            <p className="text-gray-600 text-lg mb-8">Manufacturing Workflow Management System</p>

            {/* Enter Button */}
            <button
              onClick={() => setShowApp(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-12 py-4 rounded-xl text-xl font-bold hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Enter Application →
            </button>

            {/* Info */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-gray-500 text-sm">Click the logo or button to access the order tracking system</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Application
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* York Container Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight">MSQ Order Tracker</h1>
              <p className="text-blue-100 mt-1">York Container Order Management System</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 shadow-md">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-blue-600">YORK</div>
                <div className="text-2xl font-bold text-green-500">CONTAINER</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-8 pt-6">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('backlog')}
                className={`px-6 py-3 font-semibold rounded-t-lg transition-all ${
                  activeTab === 'backlog'
                    ? 'bg-white text-green-600 shadow-sm border-t-4 border-green-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                }`}
              >
                Backlog
              </button>
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`px-6 py-3 font-semibold rounded-t-lg transition-all ${
                  activeTab === 'dashboard'
                    ? 'bg-white text-blue-600 shadow-sm border-t-4 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-3 font-semibold rounded-t-lg transition-all ${
                  activeTab === 'orders'
                    ? 'bg-white text-blue-600 shadow-sm border-t-4 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                }`}
              >
                Customer Orders
              </button>
              {/* Custom Plant tab hidden for now — uncomment to bring it back
              <button
                onClick={() => setActiveTab('custom')}
                className={`px-6 py-3 font-semibold rounded-t-lg transition-all ${
                  activeTab === 'custom'
                    ? 'bg-white text-purple-600 shadow-sm border-t-4 border-purple-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                }`}
              >
                Custom Plant
              </button>
              */}
            </div>
          </div>

          <div className="p-8">
            {activeTab === 'dashboard' ? (
              // Dashboard View
              <div>
                {!stats || data.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="bg-blue-50 rounded-lg p-8 inline-block">
                      <Upload className="mx-auto mb-4 text-blue-600" size={64} />
                      <p className="text-gray-700 text-xl font-semibold mb-2">Upload data to view dashboard</p>
                      <p className="text-gray-500">Switch to Customer Orders tab to upload your Excel file</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
                        <div className="text-blue-100 text-sm font-semibold mb-2">Next 7 Days</div>
                        <div className="text-4xl font-bold mb-1">{stats.weekMSQ.toFixed(0)}</div>
                        <div className="text-blue-100 text-sm">{stats.weekOrders} orders</div>
                      </div>

                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
                        <div className="text-green-100 text-sm font-semibold mb-2">Next 30 Days</div>
                        <div className="text-4xl font-bold mb-1">{stats.monthMSQ.toFixed(0)}</div>
                        <div className="text-green-100 text-sm">{stats.monthOrders} orders</div>
                      </div>

                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                        <div className="text-purple-100 text-sm font-semibold mb-2">Avg Daily MSQ</div>
                        <div className="text-4xl font-bold mb-1">{stats.avgDailyMSQ.toFixed(0)}</div>
                        <div className="text-purple-100 text-sm">future orders</div>
                      </div>

                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
                        <div className="text-orange-100 text-sm font-semibold mb-2">Peak Day MSQ</div>
                        <div className="text-4xl font-bold mb-1">{stats.peakMSQ.toFixed(0)}</div>
                        <div className="text-orange-100 text-sm">upcoming peak</div>
                      </div>
                    </div>

                    {/* Alerts */}
                    {(stats.overCapacityDays.length > 0 || stats.highCapacityDays.length > 0) && (
                      <div className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">⚠️ Capacity Alerts (Upcoming Orders)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {stats.overCapacityDays.length > 0 && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                                  {stats.overCapacityDays.length}
                                </div>
                                <span className="font-bold text-red-900">Over Capacity (800+ MSQ)</span>
                              </div>
                              <p className="text-red-700 text-sm">Days exceeding maximum capacity</p>
                            </div>
                          )}
                          {stats.highCapacityDays.length > 0 && (
                            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                                  {stats.highCapacityDays.length}
                                </div>
                                <span className="font-bold text-yellow-900">High Capacity (700+ MSQ)</span>
                              </div>
                              <p className="text-yellow-700 text-sm">Days approaching capacity limits</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* MSQ Trend Chart */}
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">📈 Upcoming MSQ Trend (From Today)</h3>
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="space-y-3">
                          {stats.futureGroupedData.slice(0, 10).map(([date, info]) => {
                            const percentage = (info.totalMSQ / 800) * 100;
                            const barColor = info.totalMSQ >= 800 ? 'bg-red-500' : 
                                           info.totalMSQ >= 700 ? 'bg-yellow-500' : 'bg-green-500';
                            return (
                              <div key={date}>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm font-semibold text-gray-700">{date}</span>
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600">{info.orders.length} orders</span>
                                    <span className="text-sm font-bold text-gray-900">{info.totalMSQ.toFixed(0)} MSQ</span>
                                  </div>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-8 relative overflow-hidden">
                                  <div 
                                    className={`${barColor} h-8 rounded-full transition-all flex items-center justify-center`}
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  >
                                    <span className="text-sm text-white font-bold px-2">
                                      {percentage.toFixed(0)}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Top Items & Ship To */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">🏆 Top 5 Items (Upcoming Orders)</h3>
                        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                          {stats.topItems.length > 0 ? (
                            <div className="space-y-3">
                              {stats.topItems.map(([item, msq], idx) => (
                                <div key={item} className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                    idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-600' : 'bg-blue-500'
                                  }`}>
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-gray-900">{item}</div>
                                    <div className="text-sm text-gray-600">{msq.toFixed(0)} MSQ</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No upcoming items</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">📍 Top 5 Ship To (Upcoming Orders)</h3>
                        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                          {stats.topShipTo.length > 0 ? (
                            <div className="space-y-3">
                              {stats.topShipTo.map(([location, msq], idx) => (
                                <div key={location} className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                    idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-600' : 'bg-green-500'
                                  }`}>
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-gray-900">{location}</div>
                                    <div className="text-sm text-gray-600">{msq.toFixed(0)} MSQ</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No upcoming shipments</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Upcoming High Capacity Days */}
                    {stats.upcomingHighCapacity.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">🔔 Upcoming High Capacity Days</h3>
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">MSQ</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Orders</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stats.upcomingHighCapacity.map(([date, info]) => (
                                <tr key={date} className="border-t border-gray-200">
                                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{date}</td>
                                  <td className="px-6 py-4 text-sm font-bold text-gray-900">{info.totalMSQ.toFixed(0)}</td>
                                  <td className="px-6 py-4 text-sm text-gray-700">{info.orders.length}</td>
                                  <td className="px-6 py-4">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                      info.totalMSQ >= 800 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {info.totalMSQ >= 800 ? 'Over Capacity' : 'High'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : activeTab === 'backlog' ? (
                <Backlog
                  backlogItems={backlogItems}
                  setBacklogItems={setBacklogItems}
                  showBacklogForm={showBacklogForm}
                  setShowBacklogForm={setShowBacklogForm}
                  backlogForm={backlogForm}
                  setBacklogForm={setBacklogForm}
                  handleBacklogSubmit={handleBacklogSubmit}
                  deleteBacklogItem={deleteBacklogItem}
                />
              ) : activeTab === 'custom' ? (
                <Custom />
              ) : (
              <>
                <div className="flex gap-4 items-center flex-wrap mb-6">
                  <label className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-all shadow-md hover:shadow-lg font-semibold">
                    <Upload size={20} />
                    Upload Excel File
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                  
                  {data.length > 0 && (
                    <button
                      onClick={downloadExcel}
                      className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all shadow-md hover:shadow-lg font-semibold"
                    >
                      <Download size={20} />
                      Download Excel
                    </button>
                  )}
                  
                  {fileName && (
                    <span className="text-gray-600 text-sm font-medium bg-gray-100 px-4 py-2 rounded-lg">
                      📄 {fileName}
                    </span>
                  )}
                </div>

                {data.length > 0 && (
                  <div className="flex gap-4 items-center flex-wrap mb-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 font-semibold text-sm">Filter:</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDateFilterChange('all')}
                          className={`px-4 py-2 rounded-lg transition-all text-sm font-semibold ${
                            dateFilter === 'all' 
                              ? 'bg-blue-600 text-white shadow-md' 
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          All Orders
                        </button>
                        <button
                          onClick={() => handleDateFilterChange('today')}
                          className={`px-4 py-2 rounded-lg transition-all text-sm font-semibold ${
                            dateFilter === 'today' 
                              ? 'bg-blue-600 text-white shadow-md' 
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          From Today Forward
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 font-semibold text-sm">Item:</span>
                      <select
                        value={selectedItem}
                        onChange={(e) => handleItemChange(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-sm shadow-sm"
                      >
                        <option value="all">All Items</option>
                        {itemOptions.map(item => (
                          <option key={item} value={item}>{item}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 font-semibold text-sm">Ship To:</span>
                      <select
                        value={selectedShipTo}
                        onChange={(e) => handleShipToChange(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-sm shadow-sm"
                      >
                        <option value="all">All Locations</option>
                        {shipToOptions.map(shipTo => (
                          <option key={shipTo} value={shipTo}>{shipTo}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {data.length > 0 && (
                  <div className="mb-6 flex gap-6 items-center text-sm flex-wrap bg-gray-50 p-4 rounded-lg">
                    <span className="font-bold text-gray-700">MSQ Capacity Guide:</span>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-green-100 border-2 border-green-500 rounded"></div>
                      <span className="text-gray-700 font-medium">&lt; 700 MSQ</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-yellow-100 border-2 border-yellow-500 rounded"></div>
                      <span className="text-gray-700 font-medium">700-799 MSQ</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-red-100 border-2 border-red-500 rounded"></div>
                      <span className="text-gray-700 font-medium">800+ MSQ</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {activeTab === 'orders' && Object.keys(groupedData).length > 0 && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-6 border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                  <tr>
                    <th className="px-4 py-4 text-left text-sm font-bold border-r border-blue-500">Due Date</th>
                    <th className="px-4 py-4 text-left text-sm font-bold border-r border-blue-500">Cust P/O Number</th>
                    <th className="px-4 py-4 text-left text-sm font-bold border-r border-blue-500">Ship To</th>
                    <th className="px-4 py-4 text-left text-sm font-bold border-r border-blue-500">Item Ident</th>
                    <th className="px-4 py-4 text-left text-sm font-bold border-r border-blue-500">Order#</th>
                    <th className="px-4 py-4 text-left text-sm font-bold border-r border-blue-500">Request Date</th>
                    <th className="px-4 py-4 text-left text-sm font-bold border-r border-blue-500">Item No</th>
                    <th className="px-4 py-4 text-right text-sm font-bold border-r border-blue-500">QTY (MSQ)</th>
                    <th className="px-4 py-4 text-left text-sm font-bold border-r border-blue-500">Status</th>
                    <th className="px-4 py-4 text-right text-sm font-bold">Daily Total MSQ</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(groupedData).map(key => {
                    const groupInfo = groupedData[key];
                    const bgColor = groupInfo.totalMSQ >= 800 ? 'bg-red-50' : 
                                   groupInfo.totalMSQ >= 700 ? 'bg-yellow-50' : 'bg-green-50';
                    const borderColor = groupInfo.totalMSQ >= 800 ? 'border-red-500' :
                                       groupInfo.totalMSQ >= 700 ? 'border-yellow-500' : 'border-green-500';
                    
                    return (
                      <React.Fragment key={key}>
                        {groupInfo.orders.map((order, idx) => (
                          <tr key={`${key}-${idx}`} className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${idx === 0 ? bgColor : ''}`}>
                            {idx === 0 && (
                              <td rowSpan={groupInfo.orders.length} className={`px-4 py-3 text-sm font-bold text-gray-900 border-r border-gray-300 align-top ${bgColor}`}>
                                <div className="flex items-center gap-2">
                                  <Calendar size={16} className="text-blue-600" />
                                  <span className="font-bold">{key}</span>
                                </div>
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{order.custPO}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{order.shipTo}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{order.itemIdent}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900 border-r border-gray-200">{order.orderNumber}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{order.dateRequested}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{order.itemNo}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right font-semibold border-r border-gray-200">
                              {order.qty.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{order.status}</td>
                            {idx === 0 && (
                              <td rowSpan={groupInfo.orders.length} className={`px-4 py-3 text-right align-middle border-l-4 ${borderColor} ${bgColor}`}>
                                <div className="text-3xl font-black text-gray-900">
                                  {groupInfo.totalMSQ.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-600 mt-1 font-semibold">
                                  {groupInfo.orders.length} order{groupInfo.orders.length !== 1 ? 's' : ''}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && Object.keys(groupedData).length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center mt-6 border-2 border-dashed border-gray-300">
            <Upload className="mx-auto mb-4 text-blue-600" size={64} />
            <p className="text-gray-700 text-xl font-semibold mb-2">Upload an Excel file to get started</p>
            <p className="text-gray-500 text-sm">Your file should contain: Cust. P/O Number, Item Ident, ItemNo, Order#, MSQ Ordered, Due Date, Status, Request Date, ShipTo Info</p>
          </div>
        )}
      </div>
    </div>
  );
}